import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ClientsService } from './clients.service';
import { GroupsService } from './groups.service';
import { LocalesService } from './locales.service';
import { ProfileService } from './profile.service';
import { StudentsService } from './students.service';
import { DropdownQuestion, EntityQuestion, QuestionBase, HiddenQuestion, AgentsQuestion } from 'core/components/forms';
import { CountriesService } from 'core/services/countries.service';
import { AgentsService } from 'core/services/agents.service';
import { DistributorsService } from 'core/services/distributors.service';
import { AgentsCacheService } from 'core/services/agents-cache.service';
import { VISIBLE_AGENTS } from 'core/services/assets/constants';
import { Router } from '@angular/router';

@Injectable()
export class CoreQuestionsFactoryService {
  userRole: any;

  constructor(
    public distributors: DistributorsService,
    public clients: ClientsService,
    public groups: GroupsService,
    public agentsCache: AgentsCacheService,
    public agents: AgentsService,
    public students: StudentsService,
    public profileService: ProfileService,
    public countries: CountriesService,
    public locales: LocalesService,
    public translateService: TranslateService,
    public router: Router
  ) {
    this.profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  getUserRole() {
    return this.userRole;
  }

  isClientHidden(): boolean {
    return this.userRole.scope === 'client';
  }

  getDistributorQuestion(params: any = {}): QuestionBase<string> {
    const distributor = params.distributor;
    if (this.userRole.name === 'distributorManager') {
      const questionConfig = Object.assign(
        {
          key: 'distributorId',
          label: 'nouns.distributor',
          entity: distributor,
          value: distributor ? distributor.id : null,
          dataProvider: this.distributors
        },
        params
      );
      return new EntityQuestion(questionConfig);
    } else if (
      distributor &&
      (this.userRole.name === 'superAdmin' || this.userRole.group === 'cs' || this.userRole.scope === 'partner')
    ) {
      return new HiddenQuestion({
        ...params,
        key: 'distributorId',
        value: distributor ? distributor.id : null
      });
    }
    return null;
  }

  getClientQuestion(params: any = {}): QuestionBase<string> {
    const client = params.client;
    const questionConfig = Object.assign(
      {
        key: 'clientId',
        label: 'nouns.client',
        clickTags: true,
        value: client ? client.id : null,
        entity: client,
        dataProvider: this.clients,
        entityOptions: { filters: { 'scorm.enabled': 'false' } },
        hidden: this.isClientHidden()
      },
      params
    );
    questionConfig.eventHandlers = {
      ...questionConfig.eventHandlers,
      onClickTag: (clientTag) => {
        this.router.navigate(['/clients', clientTag.value]);
      }
    };
    return new EntityQuestion(questionConfig);
  }

  getGroupQuestion(params: any = {}): QuestionBase<string> {
    const { client, group } = params;
    const questionConfig = Object.assign(
      {
        key: 'groupId',
        label: 'nouns.group',
        entityOptions: {
          filters: client ? { clientId: client.id } : null
        },
        entity: group,
        value: group ? group.id : null,
        dataProvider: this.groups,
        preload: client ? true : false
      },
      params
    );
    return new EntityQuestion(questionConfig);
  }

  getAgentQuestion(params: any = {}): QuestionBase<string> {
    const { client, group, role, hasBonus } = params;

    const agentsList = group ? group.agentIds : client ? client.agentIds : [];
    const questionConfig = Object.assign(
      {
        value: agentsList
      },
      params
    );

    if (role === this.agentsCache.ROLES.HR) {
      questionConfig.agentsObservable = this.agentsCache.getClientAgents();
    } else if (role === this.agentsCache.ROLES.DISTRIBUTOR) {
      questionConfig.agentsObservable = this.agentsCache.getDistributorAgents();
    } else {
      questionConfig.agents = this.agentsCache.getAgents(role);
    }

    if (this.userRole.name !== 'superAdmin' && (!role || !VISIBLE_AGENTS[this.userRole.name].includes(role))) {
      if (hasBonus) {
        // When a client's admin is creating a bonus group, we set client's agents as group's agents
        questionConfig.agents = agentsList.map((agentId: string) => {
          return { id: agentId };
        });
        questionConfig.hide = true;
        return new AgentsQuestion(questionConfig);
      }
      return null;
    }
    return new AgentsQuestion(questionConfig);
  }

  getUserQuestion(params: any = {}): QuestionBase<string> {
    const { client, user } = params;
    const questionConfig = Object.assign(
      {
        key: 'userId',
        label: 'nouns.username',
        entityOptions: {
          valueField: 'id',
          nameField: 'email',
          filters: client ? { clientId: client.id } : null,
          listParser: (listUser) =>
            listUser ? `${listUser.name || ''} ${listUser.surname || ''} ${listUser.email}` : ''
        },
        entity: user,
        value: user ? user.id : null,
        dataProvider: this.students,
        preload: client ? true : false
      },
      params
    );
    return new EntityQuestion(questionConfig);
  }

  getCountryQuestion(params: any = {}): QuestionBase<string> {
    const countryOptions = this.countries.getOptions();
    const questionConfig = Object.assign(
      {
        key: 'country',
        label: 'nouns.country',
        placeholder: 'actions.selectCountry',
        options: countryOptions
      },
      params
    );
    return new DropdownQuestion(questionConfig);
  }

  getTimezoneQuestion(params: any = {}): QuestionBase<string> {
    const { country } = params;
    const timezones = this.countries.getTimezoneOptions(country);
    const questionConfig = Object.assign(
      {
        key: 'timezone',
        label: 'nouns.timezone',
        options: timezones
      },
      params
    );
    questionConfig.value = questionConfig.value || this.countries.getDefaultTimezone(country);
    return new DropdownQuestion(questionConfig);
  }

  getLanguageQuestion(params: any = {}): QuestionBase<string> {
    const { game, course } = params;
    const gameLanguages = course ? course.languages : game ? game.languages : null;
    const localesOptions: any[] = gameLanguages
      ? this.locales.getAvailableLocaleOptions(gameLanguages)
      : this.locales.getOptions();
    let questionConfig;
    if (localesOptions.length === 1) {
      questionConfig = Object.assign(
        {
          key: 'lang',
          label: 'nouns.language',
          options: localesOptions
        },
        params
      );
      questionConfig.value = localesOptions[0].key;
    } else {
      questionConfig = Object.assign(
        {
          key: 'lang',
          label: 'nouns.language',
          placeholder: 'actions.selectLanguage',
          options: localesOptions
        },
        params
      );
    }

    return new DropdownQuestion(questionConfig);
  }
}
